import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtEur } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { CalendarOff, Loader2, Search, Download, ArrowLeft, FileText, AlertTriangle } from "lucide-react";
import { FngTable, fmtH } from "../components/fng/FngTable";

const selectCls = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";
const fmtPeriod = (p) => (p ? p.split("-").reverse().join("/") : "—");

const Kpi = ({ label, value, sub, tone = "slate", testid }) => (
    <div className={`rounded-lg border p-3 ${tone === "blue" ? "border-[#376DB3]/30 bg-[#376DB3]/5" : tone === "green" ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`} data-testid={testid}>
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
        <div className="font-mono-numbers text-lg font-bold text-slate-800">{value}</div>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
);

export default function FngPage() {
    const [period, setPeriod] = useState(null);
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [onlyNeg, setOnlyNeg] = useState(false);
    const [showMissing, setShowMissing] = useState(true);
    const [scanning, setScanning] = useState(false);

    const load = useCallback(async (p) => {
        try {
            const { data: d } = await api.get("/fng/summary", { params: p ? { period: p } : {} });
            setData(d);
            if (!p) setPeriod(d.period);
            setScanning(!!d.scan?.running);
        } catch { toast.error("Errore nel caricamento F.N.G."); }
    }, []);

    useEffect(() => { load(period); }, [period, load]);

    useEffect(() => {
        if (!scanning) return undefined;
        const t = setInterval(async () => {
            try {
                const { data: s } = await api.get("/fng/scan/status");
                if (!s.scan?.running) {
                    clearInterval(t);
                    setScanning(false);
                    toast.success(`Buste ${fmtPeriod(s.scan?.period)} lette: ${s.scan?.found ?? 0} su ${s.scan?.total ?? 0}`);
                    load(period);
                } else {
                    setData((d) => (d ? { ...d, scan: s.scan } : d));
                }
            } catch { clearInterval(t); }
        }, 2500);
        return () => clearInterval(t);
    }, [scanning, period, load]);

    const startScan = async (onlyUnscanned = true) => {
        try {
            await api.post("/fng/scan", { period, only_unscanned: onlyUnscanned });
            setScanning(true);
            setData((d) => (d ? { ...d, scan: { running: true, period, done: 0, total: d.month?.docs || 0 } } : d));
        } catch (e) { toast.error(e.response?.data?.detail || "Impossibile avviare la lettura"); }
    };

    const rows = useMemo(() => {
        const s = q.trim().toLowerCase();
        return (data?.rows || []).filter((r) =>
            (showMissing || r.status === "ok") &&
            (!onlyNeg || (r.fng_hours ?? 0) < 0) &&
            (!s || [r.cognome, r.nome, r.matricola].some((v) => String(v || "").toLowerCase().includes(s))));
    }, [data, q, onlyNeg, showMissing]);

    const exportCsv = () => {
        const head = ["Matricola", "Dipendente", "Ferie residui", "Ferie rateo", "ROL residui", "ROL rateo", "Ore FNG", "Paga oraria", "Valore FNG", "Busta"];
        const lines = rows.filter((r) => r.status === "ok").map((r) => [r.matricola || "", `${r.cognome} ${r.nome}`, r.ferie?.residui ?? "", r.ferie?.rateo ?? "", r.rol?.residui ?? "", r.rol?.rateo ?? "", r.fng_hours, r.hourly_rate, r.fng_value, fmtPeriod(data.period)]
            .map((v) => (typeof v === "number" ? String(v).replace(".", ",") : `"${v}"`)).join(";"));
        const blob = new Blob(["\ufeff" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `FNG_${data.period}.csv`;
        a.click();
    };

    if (!data) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    const t = data.totals || {};
    const m = data.month || {};
    const unread = Math.max(0, (m.docs || 0) - (m.scanned || 0));

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="fng-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]"><CalendarOff className="h-6 w-6" /></div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">F.N.G. — Ferie non godute</h1>
                        <p className="text-sm text-slate-500">
                            Ore Ferie e R.O.L. (residui + rateo) lette dai contatori dell'ultima busta paga in archivio, valorizzate alla paga oraria lorda. Dati riservati all'ufficio.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/acconti"><Button variant="outline" className="gap-2" data-testid="fng-back-btn"><ArrowLeft className="h-4 w-4" /> Acconti</Button></Link>
                    <select value={period || ""} onChange={(e) => setPeriod(e.target.value)} className={selectCls} data-testid="fng-period-select">
                        {(data.periods || []).map((p) => <option key={p.period} value={p.period}>Busta {fmtPeriod(p.period)} · {p.found}/{p.docs} lette</option>)}
                    </select>
                    <Button variant="outline" className="gap-2" onClick={() => startScan(true)} disabled={scanning || !period} data-testid="fng-scan-btn"
                        title={unread ? `${unread} buste del mese ancora da leggere` : "Tutte le buste del mese sono già state lette (rilegge solo le nuove)"}>
                        {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                        {scanning ? `Lettura ${data.scan?.done ?? 0}/${data.scan?.total ?? "…"}` : unread ? `Leggi ${unread} buste del mese` : "Leggi buste del mese"}
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={exportCsv} data-testid="fng-export-btn"><Download className="h-4 w-4" /> CSV</Button>
                </div>
            </div>

            {!data.period && (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="fng-empty-banner">Nessuna busta paga in archivio.</div>
            )}
            {data.period && m.found === 0 && !scanning && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="fng-unscanned-banner">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>Le buste di {fmtPeriod(data.period)} non sono ancora state lette: premi «Leggi buste del mese» ({m.docs} PDF, circa un minuto in background).</div>
                </div>
            )}

            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Kpi label="Mese di riferimento" value={fmtPeriod(data.period)} sub={`${t.with_data || 0} dipendenti letti su ${m.docs || 0} buste`} tone="blue" testid="fng-kpi-period" />
                <Kpi label="Ore ferie" value={fmtH(t.ferie_hours)} sub="residui + rateo" testid="fng-kpi-ferie" />
                <Kpi label="Ore R.O.L." value={fmtH(t.rol_hours)} sub="residui + rateo" testid="fng-kpi-rol" />
                <Kpi label="Ore F.N.G. totali" value={fmtH(t.hours)} sub={t.negative ? `${t.negative} dipendent${t.negative === 1 ? "e" : "i"} in negativo (sommat${t.negative === 1 ? "o" : "i"})` : "ferie + R.O.L."} testid="fng-kpi-hours" />
                <Kpi label="Valore F.N.G. lordo" value={fmtEur(t.value)} sub={t.without_payslip || t.unreadable ? `${t.without_payslip || 0} senza busta · ${t.unreadable || 0} non leggibili` : "ore × paga oraria lorda"} tone="green" testid="fng-kpi-value" />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-2 text-xs text-slate-600">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente o matricola…" className="h-9 w-64 pl-8" data-testid="fng-search" />
                    </div>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={onlyNeg} onChange={(e) => setOnlyNeg(e.target.checked)} data-testid="fng-only-neg" /> Solo in negativo</label>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showMissing} onChange={(e) => setShowMissing(e.target.checked)} data-testid="fng-show-missing" /> Mostra senza busta / non letti</label>
                    <span className="ml-auto text-slate-400">{rows.length} righe</span>
                </div>
                <FngTable rows={rows} period={data.period} />
            </div>
        </div>
    );
}
